// Foundations
import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';

// rxjs
import { finalize, map } from 'rxjs/operators';

// Models
import { LoadingIndicatorData } from 'src/app/models/loading-indicator-data.model';

// Services
import { AnalyticsService } from 'src/app/services/analytics.service';
import { CognitoService } from 'src/app/services/cognito.service';
import { IsEmailExistGQL } from 'src/app/services/graphql.service';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { RegisterService } from 'src/app/services/register.service';

// Components
import { ConfirmDialogComponent } from '../../_share/confirm-dialog/confirm-dialog.component';
import { StartOverDialogComponent } from '../start-over-dialog/start-over-dialog.component';

const CODE_LENGTH = 6;
const TIMER_LENGTH = 30;

@Component({
  selector: 'app-one-factor-auth',
  templateUrl: './one-factor-auth.component.html',
  styleUrls: ['./one-factor-auth.component.scss'],
})
export class OneFactorAuthComponent implements OnInit {
  codeCtrl = new FormControl([]);

  codeLength = CODE_LENGTH;
  timerLength = TIMER_LENGTH;

  continueBtnEnabled = false;
  resendCodeBtnEnabled = false;
  isCodeSent = true;
  count;
  emailAddress = '';

  constructor(
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private registerService: RegisterService,
    private cognitoService: CognitoService,
    private isEmailExistQuery: IsEmailExistGQL,
    private _analytics: AnalyticsService,
    private loadingIndicatorService: LoadingIndicatorService
  ) {}

  ngOnInit(): void {
    this._analytics.logScreen('regCodeVerificationPage');
    const { email } = this.registerService.readRegisterFormData();
    this.emailAddress = email;
    this.handleCodeInput();
    this.startCountdown();
  }

  handleCodeInput() {
    this.codeCtrl.valueChanges.subscribe((code: string[]) => {
      const codeStr = code.join('');
      if (codeStr.length === this.codeLength) {
        this.loadingIndicatorService.show({ message: 'Verifying' });
        const { email } = this.registerService.readRegisterFormData();
        this.codeCtrl.disable({ emitEvent: false });
        this.cognitoService
          .confirmSignUp(email, codeStr)
          .pipe(finalize(() => this.loadingIndicatorService.hide()))
          .subscribe((status) => {
            const data: LoadingIndicatorData = {};

            if (status.result === 'SUCCESS') {
              this.continueBtnEnabled = true;
              data.message = 'Confirmed';
              data.isSuccess = true;
            } else {
              this.codeCtrl.reset();
              this.codeCtrl.enable({ emitEvent: false });
              data.message = 'Error';
              data.description = 'Invalid Code';
              data.isSuccess = false;
            }

            setTimeout(() => {
              this.loadingIndicatorService.show(data);
            });
            setTimeout(() => {
              this.loadingIndicatorService.hide();
            }, 2000);
          });
      }
    });
  }

  onContinue() {
    this._analytics.logButtonTap('regCodeVerification_continue');

    const { email, password } = this.registerService.readRegisterFormData();

    if (!this.registerService.readRegisterFormData().firstName) {
      // LOGIN FLOW
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        panelClass: 'general-dialog',
        data: {
          title: 'Account Verified',
          content: `Your account is now verified. Please proceed to login.`,
          okText: 'OK',
          okFn: () => {
            dialogRef.close();
            this.router.navigate(['/sign-in']);
          },
        },
        autoFocus: false,
        disableClose: true,
      });
    } else {
      this.loadingIndicatorService.show();
      // REGISTER FLOW
      this.cognitoService
        .signIn({ username: email, password })
        .subscribe((status) => {
          if (status.result === 'SUCCESS') {
            // "Email Check"
            this.isEmailExistQuery
              .fetch({ email })
              .pipe(
                map((res) => res.data.isEmailExist),
                finalize(() => this.loadingIndicatorService.hide())
              )
              .subscribe((isEmailExist) => {
                if (isEmailExist) {
                  this.cognitoService.signOut();
                  this.router.navigate(['/register/location']);
                } else {
                  // if email does not exist,
                  const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                    panelClass: 'general-dialog',
                    data: {
                      title: 'Email Not Found',
                      content: `Oops, it looks like we don't have your email address in our system. Are you new to Camp Bow Wow®?`,
                      cancelText: 'No',
                      cancelFn: () => {
                        // user chooses "No", delete user from Cognito userpool and open start over prompt dialog
                        dialogRef.close();
                        this.loadingIndicatorService.show();
                        this.cognitoService
                          .deleteUser({ username: email, password })
                          .pipe(
                            finalize(() => this.loadingIndicatorService.hide())
                          )
                          .subscribe((deleteStatus) => {
                            if (deleteStatus.result === 'SUCCESS') {
                              this.openStartOverDialog();
                            } else {
                              this.snackBar.open(deleteStatus.message, '', {
                                duration: 2000,
                              });
                            }
                          });
                      },
                      okText: 'Yes',
                      okFn: () => {
                        // user chooses "Yes", navigate to location selection
                        dialogRef.close();
                        this.cognitoService.signOut();
                        this.router.navigate(['/register/location']);
                      },
                    },
                    autoFocus: false,
                    disableClose: true,
                  });
                }
              });
          } else {
            this.loadingIndicatorService.hide();
            this.snackBar.open(status.message, '', { duration: 2000 });
          }
        });
    }
  }

  toggleCodeSent() {
    this._analytics.logButtonTap('regCodeVerification_resendCode');
    this.isCodeSent = true;
    this.resendCodeBtnEnabled = false;
    this.startCountdown();
    this.cognitoService.resendCode(
      this.registerService.readRegisterFormData().email
    );
  }

  startCountdown() {
    const _this = this;
    this.count = this.timerLength;
    const timer = setInterval(() => {
      if (--_this.count === 0) {
        clearInterval(timer);
        _this.isCodeSent = false;
        _this.resendCodeBtnEnabled = true;
      }
    }, 1000);
  }

  // Private Methods
  // ------------------------------------------------------------------------------------

  private openStartOverDialog() {
    this._analytics.logScreen('regStartOverPage');

    const dialogRef = this.dialog.open(StartOverDialogComponent, {
      panelClass: 'general-dialog',
      data: {
        startOverFn: () => {
          this._analytics.logButtonTap('regStartOver_startOver');
          dialogRef.close();
          this.router.navigate(['../info'], { relativeTo: this.route });
        },
        contactCbwFn: () => {
          this._analytics.logButtonTap('regStartOver_contactLocalCBW');
          dialogRef.close();
          window.open('https://www.campbowwow.com/locations/', '_blank');
        },
      },
      autoFocus: false,
      disableClose: true,
    });
  }
}
